import { useEffect, useState } from 'react';
import { Button, Form, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { pruneExpiredOrdersIDB } from 'src/services/offline/outboxClient';
import {
  APP_EVENTS,
  captureAppEvent,
  TABLE_CONTEXT,
} from 'src/services/posthogEvents';

import useLocalStore from 'src/store/useLocalStore';

import CameraLanguageDropdown from 'src/components/custom_components/CameraLanguageDropdown';
import SitesDropdown from 'src/components/custom_components/SitesDropdown';
import OpenOrdersTable from 'src/components/NewOrderPage/OpenOrdersTable';
import NewPatientCreation from 'src/components/NewPatientCreation/NewPatientCreation';
import SearchPatientInfoFormGroup, {
  SearchPatientFormValues,
} from 'src/components/SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

import { I18NextNS } from 'src/models/Language.model';
import { Product } from 'src/models/Product.model';

import useCheckCameraConnection from 'src/hooks/useCheckCameraConnection';
import useGetExistingSiteData from 'src/hooks/useGetExistingSiteData';
import { useGetFilterOrder } from 'src/hooks/useGetFilterOrder';

import { captureExceptionWithContext } from 'src/utils/errorReporting';

async function pruneExpiredLocalOrders(): Promise<void> {
  try {
    const prunedOrderIds = await pruneExpiredOrdersIDB();
    if (prunedOrderIds.length) {
      captureAppEvent(APP_EVENTS.LOCAL_ORDERS_PRUNED, {
        count: prunedOrderIds.length,
      });
    }
  } catch (error) {
    captureExceptionWithContext(error, { operation: 'prune_expired_orders' });
  }
}

export default function TeleophthStandaloneNewExam({
  product,
  onResetBlockedChange,
}: {
  product: Product;
  onResetBlockedChange: (isResetBlocked: boolean) => void;
}) {
  const { isCameraConnected } = useCheckCameraConnection();
  const { t } = useTranslation(I18NextNS.PAGES);
  const { isLoadingSites, existingSites, getExistingSiteData } =
    useGetExistingSiteData();
  const { getFilteredOrders, ordersData, ordersWithExams, isGettingOrders } =
    useGetFilterOrder();
  const { selectedSiteId } = useLocalStore();

  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);
  const [searchValues, setSearchValues] = useState<SearchPatientFormValues>();
  const [currentPage, setCurrentPage] = useState(1);

  const showNewPatientModal = (isOpen: boolean) => {
    setIsNewPatientModalOpen(isOpen);
    onResetBlockedChange(isOpen);
  };

  const resetSearchValues = () => {
    setSearchValues(undefined);
    getFilteredOrders({ siteId: selectedSiteId, pageNumber: 0 });
    setCurrentPage(1);
  };

  const emitFilterApplied = (
    nextSearchValues: SearchPatientFormValues | undefined,
    siteId: string | undefined,
  ) => {
    captureAppEvent(APP_EVENTS.SEARCH_FILTER_APPLIED, {
      table: TABLE_CONTEXT.OPEN_ORDERS,
      has_first_name: Boolean(nextSearchValues?.firstName?.trim()),
      has_last_name: Boolean(nextSearchValues?.lastName?.trim()),
      has_id: Boolean(nextSearchValues?.id?.trim()),
      has_dob: Boolean(nextSearchValues?.dob),
      has_site_filter: Boolean(siteId),
    });
  };

  const loadOrders = async () => {
    // getFilteredOrders derives Resume vs Start from what is left in the
    // outbox, so the prune has to finish first.
    await pruneExpiredLocalOrders();
    await getFilteredOrders({
      siteId: selectedSiteId,
      pageNumber: 0,
      searchValues,
    });
  };

  useEffect(() => {
    getExistingSiteData();
  }, [getExistingSiteData]);

  useEffect(() => {
    void loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The patient modal can navigate straight into the exam flow without closing
  // itself, leaving no handler to release the block.
  useEffect(() => {
    return () => onResetBlockedChange(false);
  }, [onResetBlockedChange]);

  return (
    <>
      <Stack gap={3} className="h-100">
        <div className="d-flex flex-column flex-md-row justify-content-md-between gap-3">
          <div className="flex-grow-1">
            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
              <div>
                <h3
                  style={{ whiteSpace: 'nowrap' }}
                  data-test="exam-orders-list-page-title"
                >
                  {t('newExam.desc')}
                </h3>
              </div>
              {product === Product.TELEOPHTH_STANDALONE && (
                <Button
                  variant="primary-100"
                  className="d-flex align-items-center"
                  onClick={() => showNewPatientModal(true)}
                  data-track="exam.new-session-standalone.create-new-order"
                  data-observe
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {t('newExam.createNewOrder')}
                  </span>
                </Button>
              )}
            </div>
          </div>
          {isCameraConnected && (
            <div className="flex-shrink-0">
              <CameraLanguageDropdown />
            </div>
          )}
        </div>
        <div
          className="flex-grow-1 d-flex flex-column"
          style={{ minHeight: 0 }}
        >
          <div
            className="flex-grow-1 d-flex flex-column"
            style={{ minHeight: 0 }}
          >
            <Stack direction="horizontal" gap={3} className="mb-3">
              <div className="h-100" style={{ paddingTop: '2px' }}>
                <Form.Label>
                  {t('sites.title', { ns: I18NextNS.COMMON })}
                </Form.Label>
                <SitesDropdown
                  toggleClassName="py-2"
                  isLoadingSites={isLoadingSites}
                  existingSites={existingSites}
                  handleSiteChange={(siteId) => {
                    emitFilterApplied(searchValues, siteId);
                    getFilteredOrders({ siteId, pageNumber: 0, searchValues });
                    setCurrentPage(1);
                  }}
                />
              </div>
              <SearchPatientInfoFormGroup
                onSearchValuesChange={(searchValues) => {
                  emitFilterApplied(searchValues, selectedSiteId);
                  setSearchValues(searchValues);
                  getFilteredOrders({
                    siteId: selectedSiteId,
                    pageNumber: 0,
                    searchValues,
                  });
                  setCurrentPage(1);
                }}
                onFormReset={resetSearchValues}
                values={searchValues}
              />
            </Stack>
            <div
              className="flex-grow-1 d-flex flex-column"
              style={{ minHeight: 0 }}
            >
              <div className="flex-grow-1" style={{ minHeight: 0 }}>
                <OpenOrdersTable
                  ordersWithExams={ordersWithExams}
                  totalPages={ordersData?.totalPages}
                  isLoading={isGettingOrders}
                  onFetchOrders={(pageNumber) =>
                    getFilteredOrders({
                      siteId: selectedSiteId,
                      pageNumber,
                      searchValues,
                    })
                  }
                  onPageChange={setCurrentPage}
                  currentPage={currentPage}
                />
              </div>
            </div>
          </div>
        </div>
      </Stack>
      {isNewPatientModalOpen && (
        <NewPatientCreation
          isOpen={isNewPatientModalOpen}
          onClose={() => {
            showNewPatientModal(false);
            resetSearchValues();
          }}
          initialValues={searchValues}
        />
      )}
    </>
  );
}
